import { questions } from "../data/questions";
import type {
  AnswerMap,
  AssessmentResult,
  ExecutiveDecision,
  Recommendation,
  StrategyInsight,
} from "./types";

export const NARRATIVE_SCHEMA_VERSION = "1" as const;

export interface NarrativeFact {
  id: string;
  statement: string;
}

export interface NarrativeCandidate {
  id: string;
  title: string;
  why: string;
  evidence: string[];
  actions: string[];
}

export interface NarrativeRequest {
  schemaVersion: typeof NARRATIVE_SCHEMA_VERSION;
  context: {
    annualAwsSpend: string;
    primaryGoal: string;
    planningHorizon: string;
  };
  facts: NarrativeFact[];
  candidates: NarrativeCandidate[];
  decisionActions: Array<{ label: ExecutiveDecision["label"]; action: string }>;
}

export interface EvidenceBackedText {
  text: string;
  evidenceIds: string[];
}

export interface NarrativeDecision extends EvidenceBackedText {
  label: ExecutiveDecision["label"];
  action: string;
}

export interface NarrativeInsight extends EvidenceBackedText {
  title: string;
  tone: StrategyInsight["tone"];
}

export interface NarrativeResponse {
  headline: EvidenceBackedText;
  executivePosition: EvidenceBackedText;
  portfolioThesis: EvidenceBackedText;
  decisions: NarrativeDecision[];
  insights: NarrativeInsight[];
  recommendationOrder: string[];
}

const compactMoney = (value: number) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
  notation: "compact",
}).format(value);

const money = (value: number) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
}).format(value);

const clean = (value: string, maxLength: number) => value.replace(/\s+/g, " ").trim().slice(0, maxLength);

/**
 * Build the only payload that may leave the browser. It deliberately omits the
 * respondent, company name, source filename, account IDs, resource IDs, and raw
 * CSV rows. The model receives aggregated facts and deterministic candidates.
 */
export function buildNarrativeRequest(result: AssessmentResult, answers: AnswerMap): NarrativeRequest {
  const facts: NarrativeFact[] = [];
  const sensitiveValues = [result.context.companyName, result.context.respondentName, result.context.role, result.context.industry]
    .map((value) => value.trim())
    .filter((value) => value.length >= 2)
    .sort((left, right) => right.length - left.length);
  const scrub = (value: string) => sensitiveValues.reduce((current, sensitive) => {
    const escaped = sensitive.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return current.replace(new RegExp(escaped, "gi"), "the organization");
  }, value);
  const add = (id: string, statement: string) => facts.push({ id, statement: clean(scrub(statement), 1_600) });

  add("assessment_scope", result.costAnalysis
    ? `The assessment analyzed ${result.costAnalysis.rowCount} AWS Cost Optimization Hub recommendations across ${result.costAnalysis.accountCount} accounts and ${result.costAnalysis.regionCount} Regions.`
    : "No AWS Cost Optimization Hub CSV was analyzed, so the opportunity value is not quantified.");
  add("assessment_method", "AWS-estimated opportunity, execution readiness, and realized value are separate. AWS estimates are prioritization evidence, not a forecast or guarantee.");
  add("assessment_confidence", `${result.confidence}% of the 12 structured operating-context questions were answered.`);
  add("operating_objective", `The selected operating objective is ${result.context.primaryGoal || "not specified"}, with a ${result.context.planningHorizon || "not specified"} planning horizon.`);
  add("baseline_headline", result.executiveHeadline);
  add("baseline_position", result.executivePosition);
  add("baseline_thesis", result.portfolioThesis);
  result.executiveDecisions.forEach((decision) => add(
    `baseline_decision_${decision.label.toLowerCase().replace(/\s+/g, "_")}`,
    `${decision.label}: ${decision.action} ${decision.reason}`,
  ));
  result.insights.forEach((insight, index) => add(`baseline_insight_${index + 1}`, `${insight.title}. ${insight.body}`));

  if (result.costAnalysis) {
    const analysis = result.costAnalysis;
    add("portfolio_value", `The imported AWS-estimated opportunity is ${money(analysis.totalMonthlySavings)} per month, also formatted ${compactMoney(analysis.totalMonthlySavings)} per month, and ${money(analysis.annualizedSavings)} annualized if every recommendation were realized.`);
    add("portfolio_concentration", `${analysis.topTenSavingsShare}% of imported savings is concentrated in the top 10 recommendations.`);
    add("portfolio_tail", `${analysis.lowValueTailCount} recommendations are below $100 per month and together represent ${money(analysis.lowValueTailSavings)} per month.`);
    add("change_shape", `${money(analysis.noRestartSavings)} per month is attached to recommendations that do not require a restart, and ${money(analysis.reversibleSavings)} per month is explicitly marked reversible.`);
    if (analysis.warnings.length) add("evidence_limitations", analysis.warnings.join(" "));
  }

  result.dimensions.forEach((dimension) => {
    add(`dimension_${dimension.dimension}`, `${dimension.label} readiness is ${dimension.score} out of 100 from ${dimension.answered} of ${dimension.total} answers. ${dimension.summary}`);
  });

  questions.forEach((question) => {
    const option = question.options.find((candidate) => candidate.value === answers[question.id]);
    if (option) add(`answer_${question.id}`, `${question.prompt} Answer: ${option.label}. ${option.description}`);
  });

  result.opportunityLanes.forEach((lane) => {
    add(`lane_${lane.id}`, `${lane.label}: ${lane.valueShape}. Potential is ${lane.potential}; readiness is ${lane.readiness} at ${lane.readinessScore} out of 100. ${lane.thesis} Decision gate: ${lane.decisionGate} First move: ${lane.firstMove}`);
  });

  result.recommendations.forEach((recommendation) => {
    add(`recommendation_${recommendation.id}`, `${recommendation.title}. ${recommendation.why} Proposed actions: ${recommendation.actions.join(" ")}`);
  });

  return {
    schemaVersion: NARRATIVE_SCHEMA_VERSION,
    context: {
      annualAwsSpend: clean(result.context.annualAwsSpend, 40),
      primaryGoal: clean(result.context.primaryGoal, 40),
      planningHorizon: clean(result.context.planningHorizon, 40),
    },
    facts,
    candidates: result.recommendations.map((recommendation) => ({
      id: recommendation.id,
      title: clean(recommendation.title, 180),
      why: clean(recommendation.why, 1_200),
      evidence: recommendation.evidence.map((item) => clean(item, 320)),
      actions: recommendation.actions.map((item) => clean(item, 420)),
    })),
    decisionActions: result.executiveDecisions.map((decision) => ({
      label: decision.label,
      action: clean(decision.action, 180),
    })),
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));
const isStringArray = (value: unknown, min: number, max: number, maxChars = 100) => Array.isArray(value)
  && value.length >= min
  && value.length <= max
  && value.every((item) => typeof item === "string" && item.length > 0 && item.length <= maxChars);
const allowedLabels = new Set<ExecutiveDecision["label"]>(["Priority 1", "Priority 2", "Portfolio rule"]);
const allowedTones = new Set<StrategyInsight["tone"]>(["strength", "tension", "opportunity"]);

export function isNarrativeRequest(value: unknown): value is NarrativeRequest {
  if (!isRecord(value) || value.schemaVersion !== NARRATIVE_SCHEMA_VERSION || !isRecord(value.context)) return false;
  const context = value.context;
  if (!["annualAwsSpend", "primaryGoal", "planningHorizon"].every((key) =>
    typeof context[key] === "string" && context[key].length <= 120)) return false;
  if (!Array.isArray(value.facts) || value.facts.length < 8 || value.facts.length > 80) return false;
  if (!value.facts.every((fact) => isRecord(fact)
    && typeof fact.id === "string" && /^[a-z][A-Za-z0-9_-]{1,79}$/.test(fact.id)
    && typeof fact.statement === "string" && fact.statement.length > 0 && fact.statement.length <= 1_600)) return false;
  if (new Set(value.facts.map((fact) => isRecord(fact) ? fact.id : null)).size !== value.facts.length) return false;
  if (!Array.isArray(value.candidates) || value.candidates.length < 1 || value.candidates.length > 8) return false;
  if (!value.candidates.every((candidate) => isRecord(candidate)
    && typeof candidate.id === "string" && /^[a-z][a-z0-9-]{1,79}$/.test(candidate.id)
    && typeof candidate.title === "string" && candidate.title.length > 0 && candidate.title.length <= 180
    && typeof candidate.why === "string" && candidate.why.length > 0 && candidate.why.length <= 1_200
    && isStringArray(candidate.evidence, 1, 8, 320)
    && isStringArray(candidate.actions, 1, 6, 420))) return false;
  if (new Set(value.candidates.map((candidate) => isRecord(candidate) ? candidate.id : null)).size !== value.candidates.length) return false;
  if (!Array.isArray(value.decisionActions) || value.decisionActions.length !== 3) return false;
  const decisionLabels = value.decisionActions.map((decision) => isRecord(decision) ? decision.label : null);
  return decisionLabels.join("|") === "Priority 1|Priority 2|Portfolio rule"
    && value.decisionActions.every((decision) => isRecord(decision)
      && typeof decision.action === "string"
      && decision.action.trim().length >= 12
      && decision.action.length <= 180);
}

function isEvidenceBackedText(value: unknown, minLength: number, maxLength: number): value is EvidenceBackedText {
  return isRecord(value)
    && typeof value.text === "string"
    && value.text.trim().length >= minLength
    && value.text.length <= maxLength
    && isStringArray(value.evidenceIds, 1, 6, 80);
}

function isNarrativeDecision(value: unknown): value is NarrativeDecision {
  if (!isEvidenceBackedText(value, 40, 280)) return false;
  const decision = value as unknown as Record<string, unknown>;
  return allowedLabels.has(decision.label as ExecutiveDecision["label"])
    && typeof decision.action === "string"
    && decision.action.trim().length >= 12
    && decision.action.length <= 180;
}

function isNarrativeInsight(value: unknown): value is NarrativeInsight {
  if (!isEvidenceBackedText(value, 45, 320)) return false;
  const insight = value as unknown as Record<string, unknown>;
  return typeof insight.title === "string"
    && insight.title.trim().length >= 12
    && insight.title.length <= 100
    && allowedTones.has(insight.tone as StrategyInsight["tone"]);
}

export function isNarrativeResponse(value: unknown): value is NarrativeResponse {
  if (!isRecord(value)
    || !isEvidenceBackedText(value.headline, 20, 170)
    || !isEvidenceBackedText(value.executivePosition, 60, 450)
    || !isEvidenceBackedText(value.portfolioThesis, 60, 420)
    || !Array.isArray(value.decisions)
    || value.decisions.length !== 3
    || !Array.isArray(value.insights)
    || value.insights.length < 3
    || value.insights.length > 4
    || !isStringArray(value.recommendationOrder, 1, 8, 80)) return false;

  const labels = value.decisions.map((decision) => isRecord(decision) ? decision.label : null);
  if (labels.join("|") !== "Priority 1|Priority 2|Portfolio rule") return false;
  if (!value.decisions.every(isNarrativeDecision)) return false;

  return value.insights.every(isNarrativeInsight);
}

const numericTokens = (value: string) => value.match(/\$?\d[\d,]*(?:\.\d+)?(?:%|[KMB])?/gi) ?? [];
const normalizeNumber = (value: string) => value.toLowerCase().replace(/[,$]/g, "");
const prohibitedClaim = /(guaranteed?|will save|verified savings|realized savings (?:is|are|of)|certain to|delete production|terminate production|shut down production|without (?:review|approval)|immediately (?:delete|terminate|stop))/i;
const supportStopWords = new Set("a an and are as at be been being by for from has have if in into is it its of on or that the their them then there these they this those to was were when where which while who will with without your".split(" "));
const editorialTerms = new Set("aws estimated opportunity portfolio prioritize prioritization priority decision decisions evidence action actions organization customer value risk readiness delivery financial operational engineering safe safety move moves moving start focus protect prove validate avoid require requires required requiring clear strongest largest grouped grouping scale scaling before after because remains remain distinct separate insight insights path executive measured imported recommendation recommendations".split(" "));

function qualitativeTokens(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").split(/\s+/)
    .filter((token) => token.length >= 4 && !supportStopWords.has(token) && !/^\d+$/.test(token));
}

function statementIsSupported(item: EvidenceBackedText, factMap: Map<string, string>) {
  if (item.evidenceIds.some((id) => !factMap.has(id))) return false;
  const evidence = item.evidenceIds.map((id) => factMap.get(id) ?? "").join(" ");
  const permitted = new Set(numericTokens(evidence).map(normalizeNumber));
  const text = `${"title" in item && typeof item.title === "string" ? item.title : ""} ${"action" in item && typeof item.action === "string" ? item.action : ""} ${item.text}`;
  if (prohibitedClaim.test(text) || !numericTokens(text).every((token) => permitted.has(normalizeNumber(token)))) return false;
  const evidenceWords = new Set([...qualitativeTokens(evidence), ...editorialTerms]);
  const claimWords = qualitativeTokens(text);
  if (!claimWords.length) return false;
  const supportedWords = claimWords.filter((token) => evidenceWords.has(token)).length;
  return supportedWords / claimWords.length >= 0.65;
}

export function validateNarrativeResponseAgainstRequest(request: NarrativeRequest, response: unknown): response is NarrativeResponse {
  return narrativeResponseValidationError(request, response) === null;
}

export function narrativeResponseValidationError(request: NarrativeRequest, response: unknown): string | null {
  if (!isNarrativeResponse(response)) return "invalid_shape";
  const factMap = new Map(request.facts.map((fact) => [fact.id, fact.statement]));
  const backedItems: EvidenceBackedText[] = [response.headline, response.executivePosition, response.portfolioThesis, ...response.decisions, ...response.insights];
  const candidateIds = new Set(request.candidates.map((candidate) => candidate.id));
  if (!backedItems.every((item) => statementIsSupported(item, factMap))) return "unsupported_claim";
  const allowedActions = new Map(request.decisionActions.map((decision) => [decision.label, decision.action]));
  if (response.decisions.some((decision) => allowedActions.get(decision.label) !== decision.action)) return "unapproved_decision_action";
  const expectedOrder = request.candidates.map((candidate) => candidate.id);
  const orderIsValid = response.recommendationOrder.length === candidateIds.size
    && response.recommendationOrder.every((id, index) => id === expectedOrder[index]);
  return orderIsValid ? null : "invalid_recommendation_order";
}

export function applyValidatedNarrative(result: AssessmentResult, response: NarrativeResponse, facts: NarrativeFact[]): AssessmentResult | null {
  const validationRequest: NarrativeRequest = {
    schemaVersion: NARRATIVE_SCHEMA_VERSION,
    context: { annualAwsSpend: "", primaryGoal: "", planningHorizon: "" },
    facts,
    candidates: result.recommendations.map((recommendation) => ({
      id: recommendation.id,
      title: recommendation.title,
      why: recommendation.why,
      evidence: recommendation.evidence,
      actions: recommendation.actions,
    })),
    decisionActions: result.executiveDecisions.map(({ label, action }) => ({ label, action })),
  };
  if (!validateNarrativeResponseAgainstRequest(validationRequest, response)) return null;

  const recommendationMap = new Map(result.recommendations.map((recommendation) => [recommendation.id, recommendation]));
  const decisionMap = new Map(result.executiveDecisions.map((decision) => [decision.label, decision]));
  const recommendations = response.recommendationOrder
    .map((id) => recommendationMap.get(id))
    .filter((item): item is Recommendation => Boolean(item))
    .map((recommendation, index) => ({ ...recommendation, priority: index + 1 }));

  return {
    ...result,
    executiveHeadline: response.headline.text.trim(),
    executivePosition: response.executivePosition.text.trim(),
    portfolioThesis: response.portfolioThesis.text.trim(),
    executiveDecisions: response.decisions.map((decision) => ({
      ...decisionMap.get(decision.label)!,
      label: decision.label,
      action: decision.action.trim(),
      reason: decision.text.trim(),
    })),
    insights: response.insights.map((insight, index) => ({
      ...(result.insights[index] ?? result.insights[0]),
      title: insight.title.trim(),
      body: insight.text.trim(),
      tone: insight.tone,
    })),
    recommendations,
  };
}

interface EnhanceOptions {
  endpoint?: string;
  fetcher?: typeof fetch;
  timeoutMs?: number;
}

export async function enhanceAssessmentNarrative(
  result: AssessmentResult,
  answers: AnswerMap,
  options: EnhanceOptions = {},
): Promise<AssessmentResult> {
  const defaultEndpoint = import.meta.env.PROD
    ? "https://finops-insight-api.driven-servicebridge.workers.dev/v1/narrative"
    : "";
  const endpoint = options.endpoint ?? import.meta.env.VITE_AI_NARRATIVE_ENDPOINT ?? defaultEndpoint;
  if (!endpoint) return result;

  const request = buildNarrativeRequest(result, answers);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 38_000);
  try {
    const response = await (options.fetcher ?? fetch)(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    if (!response.ok) return result;
    const payload: unknown = await response.json();
    if (!isNarrativeResponse(payload)) return result;
    return applyValidatedNarrative(result, payload, request.facts) ?? result;
  } catch {
    return result;
  } finally {
    clearTimeout(timeout);
  }
}
